import React from 'react';
import useCollectionMaterialUploadQueueStore, {getQueuedFile} from './useCollectionMaterialUploadQueueStore';
import {
    createCollectionMaterialFromFile,
    CollectionMaterialUploadDuplicateError,
    fetchCollectionMaterialUploadQueue,
} from '../collectionTaskApi';

function isAbortError(error) {
    return error?.name === 'AbortError' || error?.name === 'CanceledError';
}

/**
 * Background upload processor for the Collection Material upload queue. Mirrors Sources'
 * useSourcesUploadQueue.js, simplified for CM: file-only (no folder resolution / web imports),
 * concurrency=1 serial uploads, server hydration + 5s polling while any item is 'processing'.
 *
 * Intentionally mounted once at CollectionLayout scope (not per-task-detail-page) so uploads
 * (and their background pipeline polling) keep running across task/tab navigation within
 * `/collection/*` — matches Sources' upload queue surviving folder navigation.
 */
export default function useCollectionMaterialUploadQueue({enabled = true, onUploaded, onDuplicate} = {}) {
    const items = useCollectionMaterialUploadQueueStore((state) => state.items);
    const paused = useCollectionMaterialUploadQueueStore((state) => state.paused);
    const concurrency = useCollectionMaterialUploadQueueStore((state) => state.concurrency);
    const hydrateFromServer = useCollectionMaterialUploadQueueStore((state) => state.hydrateFromServer);
    const markUploading = useCollectionMaterialUploadQueueStore((state) => state.markUploading);
    const markProgress = useCollectionMaterialUploadQueueStore((state) => state.markProgress);
    const markSuccess = useCollectionMaterialUploadQueueStore((state) => state.markSuccess);
    const markFailed = useCollectionMaterialUploadQueueStore((state) => state.markFailed);
    const markDuplicate = useCollectionMaterialUploadQueueStore((state) => state.markDuplicate);
    const markCancelled = useCollectionMaterialUploadQueueStore((state) => state.markCancelled);
    const markPaused = useCollectionMaterialUploadQueueStore((state) => state.markPaused);
    const clearAll = useCollectionMaterialUploadQueueStore((state) => state.clearAll);

    const inFlightControllersRef = React.useRef(new Map());

    // On mount, fetch the server's in-flight items and merge them into the queue (non-fatal on
    // failure). Runs once per mount, not a polling loop.
    React.useEffect(() => {
        if (!enabled) {
            return;
        }

        let cancelled = false;
        const controller = new AbortController();

        fetchCollectionMaterialUploadQueue({signal: controller.signal})
            .then((serverItems) => {
                if (!cancelled) {
                    hydrateFromServer(serverItems);
                }
            })
            .catch(() => {});

        return () => {
            cancelled = true;
            controller.abort();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled]); // intentionally omit hydrateFromServer (stable store action)

    // Poll every 5s while any item is 'processing' (pipeline running after the upload request
    // returned). Stops automatically once no items are left processing.
    React.useEffect(() => {
        if (!enabled) {
            return;
        }

        const hasProcessing = items.some((item) => item.status === 'processing' && item.materialId);
        if (!hasProcessing) {
            return;
        }

        let cancelled = false;
        const tid = setTimeout(async () => {
            if (cancelled) return;
            try {
                const serverItems = await fetchCollectionMaterialUploadQueue({});
                if (!cancelled) {
                    hydrateFromServer(serverItems);
                }
            } catch (_) {
                // Non-fatal — next tick will retry
            }
        }, 5000);

        return () => {
            cancelled = true;
            clearTimeout(tid);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, items]); // items dep re-arms the timer after each update

    React.useEffect(() => {
        if (!enabled || !paused) {
            return;
        }
        const inFlight = Array.from(inFlightControllersRef.current.entries());
        inFlight.forEach(([itemId, controller]) => {
            controller.abort();
            markPaused(itemId);
        });
    }, [enabled, markPaused, paused]);

    const startUpload = React.useCallback(async (item) => {
        if (!item?.id || inFlightControllersRef.current.has(item.id)) {
            return;
        }

        const file = getQueuedFile(item.fileKey);
        if (!file) {
            markFailed(item.id, 'File data is unavailable. Add the file again and retry.');
            return;
        }
        if (!item.collectionTaskId) {
            markFailed(item.id, 'Collection Task is required to upload files.');
            return;
        }

        const controller = new AbortController();
        inFlightControllersRef.current.set(item.id, controller);
        markUploading(item.id);

        try {
            const material = await createCollectionMaterialFromFile({
                collectionTaskId: item.collectionTaskId,
                file,
                title: item.title || undefined,
                collectionMethod: item.collectionMethod,
                classificationLevel: item.classificationLevel || null,
                distribution: item.distribution,
                compartment: item.compartment,
                signal: controller.signal,
                onProgress: ({progress}) => markProgress(item.id, progress),
            });

            markSuccess(item.id, {
                materialId: material?.id || null,
                canonicalItemId: material?.dataItemId != null ? String(material.dataItemId) : null,
            });
            onUploaded?.(material);
        } catch (error) {
            if (isAbortError(error)) {
                const latestState = useCollectionMaterialUploadQueueStore.getState();
                const latestItem = latestState.items.find((candidate) => candidate.id === item.id);
                if (!latestItem || latestItem.status === 'paused' || latestItem.status === 'cancelled') {
                    return;
                }
                if (latestState.paused) {
                    markPaused(item.id);
                    return;
                }
                markCancelled(item.id, 'Canceled');
                return;
            }

            if (error instanceof CollectionMaterialUploadDuplicateError) {
                markDuplicate(item.id, error.message, error.referenceId);
                onDuplicate?.({fileName: file?.name || item.name, referenceId: error.referenceId, message: error.message});
                return;
            }

            markFailed(item.id, error?.message || 'Upload failed');
        } finally {
            inFlightControllersRef.current.delete(item.id);
        }
    }, [markCancelled, markDuplicate, markFailed, markPaused, markProgress, markSuccess, markUploading, onDuplicate, onUploaded]);

    React.useEffect(() => {
        if (!enabled || paused) {
            return;
        }

        const uploadingCount = items.filter((item) => item.status === 'uploading').length;
        const slots = Math.max(0, concurrency - uploadingCount);
        if (!slots) {
            return;
        }

        const queued = items
            .filter((item) => item.status === 'queued' && !inFlightControllersRef.current.has(item.id))
            .slice(0, slots);

        queued.forEach((item) => {
            startUpload(item).catch(() => {});
        });
    }, [concurrency, enabled, items, paused, startUpload]);

    const cancelItem = React.useCallback((itemId) => {
        const controller = inFlightControllersRef.current.get(itemId);
        if (controller) {
            controller.abort();
        } else {
            markCancelled(itemId, 'Canceled');
        }
    }, [markCancelled]);

    const clearQueue = React.useCallback(() => {
        const inFlight = Array.from(inFlightControllersRef.current.values());
        inFlight.forEach((controller) => controller.abort());
        inFlightControllersRef.current.clear();
        clearAll();
    }, [clearAll]);

    return {
        cancelItem,
        clearQueue,
    };
}
